/**
 * STEP — Axios Instance with JWT Interceptors
 * Automatically:
 *   - Attaches Bearer token to every request
 *   - Refreshes expired access tokens using the refresh token
 *   - Redirects to login on 401 if refresh fails
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@store/authStore";
import type { TenantInfo } from "@store/authStore";
import { getTenantSlug } from "@/lib/tenant";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";
const LOGIN_PATH = "/auth/login/";
const REFRESH_PATH = "/auth/token/refresh/";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "X-App-Platform": "web",
    "X-App-Version": import.meta.env.VITE_APP_VERSION || "1.0.0",
  },
  timeout: 15000,
});

// --- Request Interceptor: Attach Access Token + Tenant Header ---
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken, tenant } = useAuthStore.getState();
    const isLoginRequest = config.url?.includes(LOGIN_PATH);

    // Authorization: skip on login (no token exists yet)
    if (accessToken && !isLoginRequest) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Tenant slug:
    //   Login requests  → read from URL hostname only, so the backend enforces
    //                      strict tenant-scoped auth for {slug}.step.uz logins.
    //                      No stored-slug fallback — avoids cross-tenant login.
    //   All other requests → prefer URL slug, fall back to stored slug.
    const urlSlug = getTenantSlug();
    if (isLoginRequest) {
      if (urlSlug) config.headers["X-Tenant-Slug"] = urlSlug;
    } else {
      const effectiveSlug = urlSlug || tenant?.slug;
      if (effectiveSlug) config.headers["X-Tenant-Slug"] = effectiveSlug;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Records why a session ended so the login page can show a banner after the
// forced logout (parity with the mobile app's session-terminated message).
function markSessionEnded(reason: "terminated" | "expired") {
  try {
    sessionStorage.setItem("step-session-ended", reason);
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

// --- Response Interceptor: Handle 401 & Token Refresh ---
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Performs the actual token refresh network call and stores the new tokens.
// Returns the new access token on success (throws on failure).
async function performRefresh(): Promise<string> {
  const { refreshToken, tenant } = useAuthStore.getState();
  if (!refreshToken) {
    throw new Error("no-refresh-token");
  }
  const refreshHeaders: Record<string, string> = {};
  if (tenant?.slug) {
    refreshHeaders["X-Tenant-Slug"] = tenant.slug;
  }
  const { data } = await axios.post(
    `${API_BASE_URL}/auth/token/refresh/`,
    { refresh: refreshToken },
    { headers: refreshHeaders }
  );

  const newAccessToken = data.data?.access || data.access;
  const newRefreshToken = data.data?.refresh || data.refresh || refreshToken;
  useAuthStore.getState().setTokens(newAccessToken, newRefreshToken);
  if (typeof window !== "undefined" && window.electronAPI) {
    window.electronAPI.setSetting("auth_token", newAccessToken).catch(console.error);
    window.electronAPI.setSetting("refresh_token", newRefreshToken).catch(console.error);
  }
  return newAccessToken;
}

// Cross-tab single-flight wrapper. When the Web Locks API is available, only one
// tab holds the "step-token-refresh" lock at a time; sibling tabs block on the
// lock and, once it releases, read the token the winning tab already stored
// (avoiding a redundant refresh). Falls back to a plain refresh where the API is
// unavailable (the in-tab isRefreshing guard still prevents duplicate refreshes
// within a single tab).
async function refreshWithCrossTabLock(): Promise<string> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks || typeof locks.request !== "function") {
    return performRefresh();
  }
  return locks.request("step-token-refresh", async () => {
    // Holding the lock serializes refreshes across tabs. A sibling tab may have
    // refreshed while we waited, but with a NON-rotating refresh token running
    // performRefresh again is harmless (it just mints a fresh access token from
    // the same still-valid refresh token). Keeping this simple avoids subtle
    // read-after-write races on the persisted store.
    return performRefresh();
  });
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const isLoginRequest = originalRequest?.url?.includes(LOGIN_PATH);
    const isRefreshRequest = originalRequest?.url?.includes(REFRESH_PATH);

    // Handle account suspension / grace period 403s
    if (error.response?.status === 403) {
      const data = error.response.data as Record<string, unknown> | undefined;
      const code = data?.code as string | undefined;

      if (code === "ACCOUNT_SUSPENDED") {
        // Force a fresh tenant fetch so SuspendedGuard re-evaluates
        try {
          const res = await axios.get(
            `${API_BASE_URL}/tenants/me/`,
            { headers: { Authorization: `Bearer ${useAuthStore.getState().accessToken}`, "X-Tenant-Slug": useAuthStore.getState().tenant?.slug ?? "" } }
          );
          const fresh = (res.data as { data: TenantInfo }).data;
          useAuthStore.getState().setTenant(fresh);
        } catch (_) {}
      }

      if (code === "GRACE_PERIOD") {
        // Attach a human-readable message so callers can show it in a toast
        const enriched = new Error(
          (data?.message as string) ||
            "Your subscription has expired. Renew to continue."
        ) as Error & { isGracePeriod: boolean };
        enriched.isGracePeriod = true;
        return Promise.reject(enriched);
      }
    }

    // Handle SESSION_TERMINATED — a remote kill; logout immediately, no retry
    if (error.response?.status === 401) {
      const data401 = error.response.data as Record<string, unknown> | undefined;
      if ((data401 as Record<string, unknown> | undefined)?.code === "SESSION_TERMINATED") {
        markSessionEnded("terminated");
        useAuthStore.getState().logout();
        return Promise.reject(error);
      }
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isLoginRequest &&
      !isRefreshRequest
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // The access token this request was originally sent with. If, by the time
      // a refresh fails, the store holds a DIFFERENT access token, then another
      // tab already refreshed successfully and we should retry rather than log
      // out. (Bearer prefix stripped for a clean comparison.)
      const requestAuthHeader = originalRequest.headers?.Authorization;
      const requestAccessToken =
        typeof requestAuthHeader === "string"
          ? requestAuthHeader.replace(/^Bearer\s+/i, "")
          : undefined;

      const staleRefreshToken = useAuthStore.getState().refreshToken;
      if (!staleRefreshToken) {
        markSessionEnded("expired");
        useAuthStore.getState().logout();
        processQueue(error, null);
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const newAccessToken = await refreshWithCrossTabLock();

        processQueue(null, newAccessToken);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;

        const refreshResponse = (refreshError as AxiosError).response;
        // A kill detected at refresh time returns SESSION_TERMINATED — an
        // unambiguous remote sign-out; always honour it.
        const rcode = (refreshResponse?.data as
          | Record<string, unknown>
          | undefined)?.code;

        if (rcode !== "SESSION_TERMINATED") {
          // Re-read the store: a sibling tab (or the cross-tab lock holder) may
          // have refreshed successfully while our own refresh was racing/failing.
          // If the current access token differs from the one this request went
          // out with, there IS a newer valid token — retry once instead of
          // logging the user out.
          const currentAccessToken = useAuthStore.getState().accessToken;
          if (
            currentAccessToken &&
            currentAccessToken !== requestAccessToken
          ) {
            processQueue(null, currentAccessToken);
            originalRequest.headers.Authorization = `Bearer ${currentAccessToken}`;
            return api(originalRequest);
          }
        }

        // Offline-tolerant refresh: only log the user out on a GENUINE auth
        // rejection — the refresh token is actually dead. That is either a
        // SESSION_TERMINATED body code, or an HTTP 401/400 (invalid/expired
        // refresh token). A network blip, timeout, missing response, or a 5xx
        // server error is transient: keep the session/tokens intact and reject
        // the original request so the caller can retry later. This matches the
        // Telegram-style "stay logged in until you log out" behaviour.
        const isAuthRejection =
          rcode === "SESSION_TERMINATED" ||
          refreshResponse?.status === 401 ||
          refreshResponse?.status === 400;

        if (!isAuthRejection) {
          processQueue(refreshError as AxiosError, null);
          return Promise.reject(refreshError);
        }

        processQueue(refreshError as AxiosError, null);
        markSessionEnded(rcode === "SESSION_TERMINATED" ? "terminated" : "expired");
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Typed helper functions for API calls
export const apiGet = <T>(url: string, params?: Record<string, unknown>) =>
  api.get<{ success: boolean; data: T; message: string }>(url, { params });

export const apiPost = <T>(url: string, data?: unknown) =>
  api.post<{ success: boolean; data: T; message: string }>(url, data);

export const apiPatch = <T>(url: string, data?: unknown) =>
  api.patch<{ success: boolean; data: T; message: string }>(url, data);

export const apiPut = <T>(url: string, data?: unknown) =>
  api.put<{ success: boolean; data: T; message: string }>(url, data);

export const apiDelete = <T>(url: string) =>
  api.delete<{ success: boolean; data: T; message: string }>(url);
